import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { load } from "@cashfreepayments/cashfree-js";
import "./BillDetailsModal.scss";
import { postRequestNew } from "../../../utils/axiospostrequest";
import { useNavigate } from "react-router-dom";

// Helper function to format numbers in Indian format
const formatIndianNumber = (number) => {
  return new Intl.NumberFormat("en-IN").format(number);
};

const BillDetailsSec = ({
  onClose,
  parentOnClose,
  planName,
  amount,
  gst,
  total,
  planId,
  days,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [cashfree, setCashfree] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const initializeSDK = async () => {
      try {
        const sdk = await load({ mode: "production" });
        if (isMounted) {
          setCashfree(sdk);
        }
      } catch (error) {
        console.error("Failed to load Cashfree SDK:", error);
        toast.error("Failed to initialize payment gateway.");
      }
    };

    initializeSDK();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePayment = async () => {
    setIsLoading(true);
    let endPoint = "";
    if (!planId) {
      endPoint = `https://ballersapi.ballerslife.co/api/v1/memberships/payments/free_trial_payment/create_an_order`;
    } else {
      endPoint = `https://ballersapi.ballerslife.co/api/v1/memberships/payments/create_an_order_v2?plan_id=${planId}`;
    }

    const token = localStorage.getItem("access_token");

    try {
      const res = await fetch(endPoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const responseData = await res.json();
      const paymentSessionId = responseData.payment_session_id;
      setOrderId(responseData.order_id || `ORD-${Date.now()}`);

      if (!paymentSessionId) {
        console.error("Payment session ID is missing in the response.");
        toast.error("Failed to initiate payment. Please try again.");
        setIsLoading(false);
        return;
      }

      if (!cashfree) {
        toast.error("Payment gateway not initialized. Please try again.");
        setIsLoading(false);
        return;
      }

      const checkoutOptions = {
        paymentSessionId: paymentSessionId,
        redirectTarget: "_modal",
      };

      cashfree.checkout(checkoutOptions).then((result) => {
        setIsLoading(false);
        if (result.error) {
          console.error("Payment error:", result.error);
          setPaymentStatus("failed");
        } else if (result.redirect) {
          console.log("Payment will be redirected.");
        } else if (result.paymentDetails) {
          console.log(
            "Payment completed successfully:",
            result.paymentDetails.paymentMessage
          );
          setPaymentStatus("success");
        }
      });
    } catch (error) {
      console.error("Error during payment initiation:", error);
      toast.error("An error occurred. Please try again later.");
      setIsLoading(false);
    }
  };
  const reloadPage = () => {
    window.location.reload();
  };

  const SuccessPopup = () => (
    <div className="payment-popup success">
      <button
        className="close-button"
        onClick={() => {
          onClose();
          parentOnClose(); // Close both modals
        }}
        aria-label="Close popup"
      >
        ×
      </button>
      <div className="popup-icon success-icon">
        <span>✔</span>
      </div>
      <p className="payment_heading">You're officially a Baller!</p>
      <p className="popup-subtext">
        Game on! Your membership is now active, unlimited access, no extra fees,
        just pure play. Time to hit the court!
      </p>
      <p className="popup-subtext">
        Order ID: {orderId}
        <br />
        <br />
        Amount: Rs {formatIndianNumber(total)}
      </p>
      <button
        className="modal-button"
        onClick={() => {
          reloadPage();
          onClose();
          parentOnClose();
        }}
      >
        Start Booking
      </button>
    </div>
  );

  const FailurePopup = () => (
    <div className="payment-popup failure">
      <button
        className="close-button"
        onClick={onClose}
        aria-label="Close popup"
      >
        ×
      </button>
      <div className="popup-icon failure-icon">
        <span>✖</span>
      </div>
      <p className="payment_heading">Oops, that didn't go through.</p>
      <p className="popup-subtext">
        Looks like something went wrong. No stress, try again or switch payment
        methods, we've got a spot waiting for you!
      </p>
      <p className="popup-subtext">
        Order ID: {orderId}
        <br />
        Amount: Rs {formatIndianNumber(total)}
      </p>
      <button className="modal-button" onClick={() => handlePayment()}>
        Retry Payment
      </button>
    </div>
  );

  return (
    <div className="bill-modal">
      <div className="bill-modal-content">
        {paymentStatus === "success" ? (
          <SuccessPopup />
        ) : paymentStatus === "failed" ? (
          <FailurePopup />
        ) : (
          <>
            <div className="bill-header">
              <span className="close-btn-billingModal" onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-chevron-left-icon lucide-chevron-left"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </span>
              <p>Bill Details:</p>
            </div>
            <div className="bill-items">
              <div className="bill-item">
                <span>{planName}</span>
                <span>₹{formatIndianNumber(amount)}</span>
              </div>
              <div className="bill-item">
                <span>GST (18%)</span>
                <span>₹{formatIndianNumber(gst)}</span>
              </div>
              <div className="bill-total">
                <span>Total</span>
                <span>₹{formatIndianNumber(total)}</span>
              </div>
            </div>
            <div className="refund-policy">
              <h3>{planId ? "Refund Policy" : " Terms & Conditions"}</h3>
              <p>
                {planId ? (
                  "Memberships are non-refundable, but totally worth it"
                ) : (
                  <>
                    <span style={{ marginRight: "14px" }}>• {days}</span>
                    <span style={{ marginRight: "14px" }}>
                      • 1 hour per day
                    </span>
                    <span style={{ marginRight: "14px" }}>
                      • Non refundable
                    </span>
                  </>
                )}
              </p>
            </div>
            <button
              className="pay-now-btn"
              onClick={handlePayment}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Pay Now!"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillDetailsSec;
